import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  DatePicker,
  Descriptions,
  Form,
  Modal,
  Progress,
  Select,
  Space,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  DownloadOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  excelService,
  type CreateExcelExportPayload,
  type ExcelExportEntity,
} from '@/services/excel.service';
import type { ExportFormat, ExportJob, ExportJobStatus } from '@/services/export.service';
import { getApiError } from '@/utils/apiError';
import { formatDateTime } from '@/utils/format';

const { Text } = Typography;
const ACTIVE_EXPORT_STATUSES = new Set<ExportJobStatus>(['pending', 'processing']);

interface ExportModalProps {
  entity: ExcelExportEntity;
  open: boolean;
  onClose: () => void;
}

interface ExportFormValues {
  format: ExportFormat;
  date_range?: [Dayjs, Dayjs];
  column_profile_key?: string;
}

interface StatusMeta {
  label: string;
  color: 'default' | 'processing' | 'success' | 'error';
  progress: number;
  progressStatus: 'normal' | 'active' | 'success' | 'exception';
  icon: React.ReactNode;
}

const ENTITY_LABEL: Record<ExcelExportEntity, string> = {
  products: 'sản phẩm',
  customers: 'khách hàng',
  suppliers: 'nhà cung cấp',
};

function getStatusMeta(status: ExportJobStatus): StatusMeta {
  switch (status) {
    case 'pending':
      return {
        label: 'Đang chờ',
        color: 'default',
        progress: 15,
        progressStatus: 'normal',
        icon: <ClockCircleOutlined />,
      };
    case 'processing':
      return {
        label: 'Đang xử lý',
        color: 'processing',
        progress: 70,
        progressStatus: 'active',
        icon: <LoadingOutlined spin />,
      };
    case 'completed':
      return {
        label: 'Hoàn thành',
        color: 'success',
        progress: 100,
        progressStatus: 'success',
        icon: <CheckCircleOutlined />,
      };
    case 'failed':
      return {
        label: 'Thất bại',
        color: 'error',
        progress: 100,
        progressStatus: 'exception',
        icon: <CloseCircleOutlined />,
      };
  }
}

function formatRemainingTime(expiresAt: string | null): string {
  if (!expiresAt) return '—';
  const remainingMs = new Date(expiresAt).getTime() - Date.now();
  if (remainingMs <= 0) return 'Đã hết hạn';

  const totalMinutes = Math.ceil(remainingMs / (60 * 1000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours <= 0) return `Còn ${minutes} phút`;
  if (minutes === 0) return `Còn ${hours} giờ`;
  return `Còn ${hours} giờ ${minutes} phút`;
}

function buildPayload(entity: ExcelExportEntity, values: ExportFormValues): CreateExcelExportPayload {
  return {
    entity,
    format: values.format,
    column_profile_key: values.column_profile_key,
    ...(values.date_range?.[0] && values.date_range?.[1]
      ? {
          date_range: {
            from: values.date_range[0].format('YYYY-MM-DD'),
            to: values.date_range[1].format('YYYY-MM-DD'),
          },
        }
      : {}),
  };
}

export function ExportModal({ entity, open, onClose }: ExportModalProps) {
  const [form] = Form.useForm<ExportFormValues>();
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  const manifestQuery = useQuery({
    queryKey: ['excel', 'manifest', entity],
    queryFn: () => excelService.getManifest(entity),
    enabled: open,
    retry: 0,
  });

  const createMutation = useMutation({
    mutationFn: excelService.createExportJob,
    onSuccess: (job) => {
      setCurrentJobId(job.export_id);
      message.success('Đã tạo yêu cầu xuất dữ liệu');
    },
  });

  const currentJobQuery = useQuery({
    queryKey: ['excel', 'export-jobs', currentJobId],
    queryFn: () => excelService.getExportJob(currentJobId ?? ''),
    enabled: Boolean(currentJobId),
    retry: 0,
    refetchInterval: (query) => {
      const data = query.state.data as ExportJob | undefined;
      if (!data) return 3000;
      return ACTIVE_EXPORT_STATUSES.has(data.status) ? 3000 : false;
    },
  });

  const currentJob = currentJobQuery.data ?? null;
  const statusMeta = currentJob ? getStatusMeta(currentJob.status) : null;
  const formatOptions = manifestQuery.data?.export?.formats ?? ['xlsx', 'csv', 'json'];
  const columnProfiles = manifestQuery.data?.export?.column_profiles ?? [];

  useEffect(() => {
    if (!open) {
      setCurrentJobId(null);
      setDownloading(false);
      form.resetFields();
      return;
    }
    form.setFieldsValue({ format: 'xlsx' });
  }, [form, open]);

  useEffect(() => {
    if (columnProfiles.length === 0) {
      form.setFieldValue('column_profile_key', undefined);
      return;
    }

    const current = form.getFieldValue('column_profile_key') as string | undefined;
    if (!current || !columnProfiles.some((profile) => profile.key === current)) {
      form.setFieldValue('column_profile_key', columnProfiles[0]?.key);
    }
  }, [columnProfiles, form]);

  const canSubmit = useMemo(() => !currentJobId, [currentJobId]);

  const handleStart = async () => {
    try {
      const values = await form.validateFields();
      await createMutation.mutateAsync(buildPayload(entity, values));
    } catch (error) {
      const apiError = getApiError(error);
      if (apiError?.message) {
        message.error(apiError.message);
      }
    }
  };

  const handleDownload = async () => {
    if (!currentJob) return;

    setDownloading(true);
    try {
      const detail = await excelService.getExportJob(currentJob.export_id);
      if (!detail.download_url) {
        message.warning('File export chưa sẵn sàng');
        return;
      }
      window.open(detail.download_url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      message.error(getApiError(error)?.message ?? 'Không thể tải file export');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Modal
      title={`Xuất ${ENTITY_LABEL[entity]} từ Excel`}
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      width={720}
    >
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Alert
          type="info"
          showIcon
          message={`Tạo job xuất dữ liệu cho ${ENTITY_LABEL[entity]}. Link tải sẽ hết hạn sau 24 giờ.`}
        />

        <Form<ExportFormValues>
          form={form}
          layout="vertical"
          initialValues={{ format: 'xlsx' }}
          disabled={!canSubmit}
        >
          <Form.Item name="format" label="Định dạng" rules={[{ required: true, message: 'Vui lòng chọn định dạng' }]}>
            <Select
              options={formatOptions.map((format) => ({
                value: format,
                label: format.toUpperCase(),
              }))}
            />
          </Form.Item>

          <Form.Item name="date_range" label="Khoảng thời gian">
            <DatePicker.RangePicker style={{ width: '100%' }} />
          </Form.Item>

          {columnProfiles.length > 0 && (
            <Form.Item name="column_profile_key" label="Bộ cột">
              <Select
                options={columnProfiles.map((profile) => ({
                  value: profile.key,
                  label: profile.label,
                }))}
              />
            </Form.Item>
          )}
        </Form>

        {currentJob && statusMeta && (
          <>
            <Descriptions
              bordered
              column={1}
              size="small"
              items={[
                {
                  key: 'status',
                  label: 'Trạng thái',
                  children: <Tag color={statusMeta.color}>{statusMeta.label}</Tag>,
                },
                { key: 'created_at', label: 'Tạo lúc', children: formatDateTime(currentJob.created_at) },
                { key: 'record_count', label: 'Số bản ghi', children: currentJob.record_count[entity] ?? '—' },
                { key: 'expires_at', label: 'Hết hạn link', children: formatRemainingTime(currentJob.expires_at) },
              ]}
            />

            <div>
              <Space align="center" style={{ marginBottom: 12 }}>
                {statusMeta.icon}
                <Text strong>{statusMeta.label}</Text>
              </Space>
              <Progress percent={statusMeta.progress} status={statusMeta.progressStatus} />
            </div>

            {currentJob.error && (
              <Alert type="error" showIcon message={currentJob.error} />
            )}
          </>
        )}

        <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
          <Button onClick={onClose}>Đóng</Button>
          {currentJob?.status === 'completed' ? (
            <Button type="primary" icon={<DownloadOutlined />} loading={downloading} onClick={() => void handleDownload()}>
              Tải file
            </Button>
          ) : (
            <Button type="primary" loading={createMutation.isPending} onClick={() => void handleStart()} disabled={!canSubmit}>
              Tạo job export
            </Button>
          )}
        </Space>
      </Space>
    </Modal>
  );
}
